import React from "react";
import {
  Wallet, GraduationCap, Users, Clock3, Sparkles, BookOpenCheck,
  UserPlus, UserCheck, ArrowUpRight, ArrowDownRight,
  UserPlus2, UserCheck2, UserX2, Users as UsersTotal,
  CalendarClock, CalendarX2, CalendarCheck2, Timer, Ban,
  Inbox, ThumbsUp, CreditCard, XOctagon, PiggyBank, Percent,
  Video, VideoOff, PlayCircle, FileVideo, AlertOctagon,
} from "lucide-react";

import { useAdminDashboard } from "../../contexts/AdminDashboardContext";

import DashboardHeader from "./components/DashboardHeader";
import DashboardFilters from "./components/DashboardFilters";
import { KpiCard, Panel } from "./components/ui";
import UsersGrowthChart from "./components/UsersGrowthChart";
import LessonsTrendChart from "./components/LessonsTrendChart";
import RevenueChart from "./components/RevenueChart";
import BookingFunnel from "./components/BookingFunnel";
import StatusDistributionChart from "./components/StatusDistributionChart";
import ActivityHeatmap from "./components/ActivityHeatmap";
import TopSubjectsChart from "./components/TopSubjectsChart";
import TopTeachersTable from "./components/TopTeachersTable";
import RecentActivityFeed from "./components/RecentActivityFeed";
import AlertsPanel from "./components/AlertsPanel";
import QuickActions from "./components/QuickActions";
import AutomaticInsights from "./components/AutomaticInsights";

/** Route guard belongs in App.jsx, same pattern as every other admin page:
 *   <Route path="AdminDashboard" element={role === 'admin' ? <AdminDashboardPage/> : <Navigate to="/" replace />} />
 * The context itself also no-ops (isAdmin=false) for non-admins as a second layer of defense,
 * and the backend re-checks role on every call regardless. */

const StatRow = ({ icon: Icon, label, value, deltaPct, invert }) => (
  <div className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
    <span className="flex items-center gap-2 text-xs text-gray-600">
      <Icon className="w-3.5 h-3.5 text-gray-400" /> {label}
    </span>
    <span className="flex items-center gap-2">
      <span className="text-xs font-bold text-gray-900">{value}</span>
      {deltaPct !== undefined && deltaPct !== null && (
        <span className={`text-[10px] font-bold ${(invert ? deltaPct <= 0 : deltaPct >= 0) ? "text-emerald-600" : "text-rose-600"}`}>
          {deltaPct > 0 ? "+" : ""}{deltaPct}%
        </span>
      )}
    </span>
  </div>
);

const formatILS = (n) =>
  new Intl.NumberFormat("he-IL", { style: "currency", currency: "ILS", maximumFractionDigits: 0 }).format(n ?? 0);

export default function AdminDashboardPage({ adminFirstName = "אנואר" }) {
  const {
    isAdmin,
    dateRange, setDateRange,
    filters, setFilters,
    granularity, setGranularity,
    subjectOptions, teacherOptions,
    summary, trends, booking,
    topTeachers, topSubjects,
    activities, alerts,
    studentsStats, meetingsStats, insights,
    resolveAlert, refreshAll, exportReport,
    isAnyLoading, lastUpdated,
  } = useAdminDashboard();

  if (!isAdmin) {
    return (
      <div dir="rtl" className="max-w-md mx-auto text-center py-24 text-gray-500">
        <p className="text-sm font-semibold">הדף זמין למנהלי מערכת בלבד.</p>
      </div>
    );
  }

  const u = summary.data?.users;
  const l = summary.data?.lessons;
  const b = summary.data?.bookingRequests;
  const r = summary.data?.revenue;
  const s = studentsStats.data;
  const mt = meetingsStats.data;

  return (
    <div dir="rtl" className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 pb-10">
      <DashboardHeader
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        lastUpdated={lastUpdated}
        refreshing={isAnyLoading}
        onRefresh={refreshAll}
        onExport={exportReport}
      />

      <DashboardFilters
        filters={filters}
        onChange={setFilters}
        subjectOptions={subjectOptions}
        teacherOptions={teacherOptions}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
      />

      {/* ---- users KPI row ---- */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 mt-5">
        <KpiCard icon={UsersTotal} label="סה״כ משתמשים" value={u?.total.value ?? "—"} deltaPct={u?.total.deltaPct} spark={u?.total.spark} accent="indigo" loading={summary.loading} />
        <KpiCard icon={GraduationCap} label="תלמידים" value={u?.students.value ?? "—"} deltaPct={u?.students.deltaPct} spark={u?.students.spark} accent="sky" loading={summary.loading} />
        <KpiCard icon={Users} label="מורים" value={u?.teachers.value ?? "—"} deltaPct={u?.teachers.deltaPct} spark={u?.teachers.spark} accent="purple" loading={summary.loading} />
        <KpiCard icon={UserPlus2} label="משתמשים חדשים" value={u?.newInRange.value ?? "—"} deltaPct={u?.newInRange.deltaPct} spark={u?.newInRange.spark} accent="emerald" loading={summary.loading} />
        <KpiCard icon={UserCheck2} label="משתמשים פעילים" value={u?.active.value ?? "—"} deltaPct={u?.active.deltaPct} spark={u?.active.spark} accent="emerald" loading={summary.loading} />
        <KpiCard icon={UserX2} label="חסומים / לא פעילים" value={u?.blocked.value ?? "—"} deltaPct={u?.blocked.deltaPct} invert spark={u?.blocked.spark} accent="rose" loading={summary.loading} />
      </div>

      {/* ---- lessons KPI row ---- */}
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-3 mt-3">
        <KpiCard icon={BookOpenCheck} label="סה״כ שיעורים" value={l?.total.value ?? "—"} deltaPct={l?.total.deltaPct} accent="indigo" loading={summary.loading} />
        <KpiCard icon={Users} label="פרטיים" value={l?.private.value ?? "—"} deltaPct={l?.private.deltaPct} accent="sky" loading={summary.loading} />
        <KpiCard icon={Users} label="קבוצתיים" value={l?.group.value ?? "—"} deltaPct={l?.group.deltaPct} accent="purple" loading={summary.loading} />
        <KpiCard icon={CalendarCheck2} label="הושלמו" value={l?.completed.value ?? "—"} deltaPct={l?.completed.deltaPct} accent="emerald" loading={summary.loading} />
        <KpiCard icon={CalendarClock} label="מתוכננים" value={l?.scheduled.value ?? "—"} deltaPct={l?.scheduled.deltaPct} accent="sky" loading={summary.loading} />
        <KpiCard icon={CalendarX2} label="בוטלו" value={l?.cancelled.value ?? "—"} deltaPct={l?.cancelled.deltaPct} invert accent="rose" loading={summary.loading} />
        <KpiCard icon={Ban} label="לא התקיימו" value={l?.noShow.value ?? "—"} deltaPct={l?.noShow.deltaPct} invert accent="rose" loading={summary.loading} />
        <KpiCard icon={Timer} label="היום" value={l?.today.value ?? "—"} accent="amber" loading={summary.loading} />
      </div>

      {/* ---- booking requests + revenue KPI row ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
        <Panel eyebrow="בקשות" title="בקשות לשיעורים פרטיים" accent="amber" loading={summary.loading} error={summary.error} onRetry={summary.retry} empty={!summary.loading && !b}>
          {b && (
            <div>
              <StatRow icon={Inbox} label="ממתינות לתגובת מורה" value={b.NO_RESPONSE.value} deltaPct={b.NO_RESPONSE.deltaPct} invert />
              <StatRow icon={ThumbsUp} label="אושרו, טרם שולמו" value={b.APPROVED.value} deltaPct={b.APPROVED.deltaPct} />
              <StatRow icon={CreditCard} label="שולמו" value={b.PAID.value} deltaPct={b.PAID.deltaPct} />
              <StatRow icon={XOctagon} label="בוטלו" value={b.CANCELLED.value} deltaPct={b.CANCELLED.deltaPct} invert />
              <StatRow icon={AlertOctagon} label="פגו תוקף" value={b.EXPIRED_12H.value} deltaPct={b.EXPIRED_12H.deltaPct} invert />
            </div>
          )}
        </Panel>

        <Panel eyebrow="הכנסות" title="הכנסות ותשלומים" accent="emerald" loading={summary.loading} error={summary.error} onRetry={summary.retry} empty={!summary.loading && !r}>
          {r && (
            <div>
              <StatRow icon={Wallet} label="סה״כ הכנסות" value={formatILS(r.total.value)} deltaPct={r.total.deltaPct} />
              <StatRow icon={Users} label="משיעורים פרטיים" value={formatILS(r.private.value)} deltaPct={r.private.deltaPct} />
              <StatRow icon={Users} label="משיעורים קבוצתיים" value={formatILS(r.group.value)} deltaPct={r.group.deltaPct} />
              <StatRow icon={XOctagon} label="עסקאות שנכשלו" value={formatILS(r.failedAmount.value)} deltaPct={r.failedAmount.deltaPct} invert />
              <StatRow icon={PiggyBank} label="החזרים וביטולים" value={formatILS(r.refunds.value)} deltaPct={r.refunds.deltaPct} invert />
              <StatRow icon={Percent} label="שיעור המרה (אושר → שולם)" value={`${r.conversionRate.value}%`} deltaPct={r.conversionRate.deltaPct} />
            </div>
          )}
        </Panel>
      </div>

      {/* ---- registrations + lessons trend ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <UsersGrowthChart
          data={trends.data?.registrations}
          granularity={granularity}
          onGranularityChange={setGranularity}
          loading={trends.loading}
          error={trends.error}
          onRetry={trends.retry}
        />
        <LessonsTrendChart data={trends.data?.lessons} loading={trends.loading} error={trends.error} onRetry={trends.retry} />
      </div>

      {/* ---- revenue + funnel ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <RevenueChart data={trends.data?.revenue} loading={trends.loading} error={trends.error} onRetry={trends.retry} />
        <BookingFunnel data={booking.data?.funnel} loading={booking.loading} error={booking.error} onRetry={booking.retry} />
      </div>

      {/* ---- status donuts + heatmap ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <StatusDistributionChart
          requestData={booking.data?.donut}
          lessonData={trends.data?.lessonStatusDonut}
          loading={booking.loading || trends.loading}
          error={booking.error || trends.error}
          onRetry={() => { booking.retry(); trends.retry(); }}
        />
        <ActivityHeatmap data={trends.data?.heatmap} loading={trends.loading} error={trends.error} onRetry={trends.retry} />
      </div>

      {/* ---- top subjects ---- */}
      <div className="mt-4">
        <TopSubjectsChart
          data={topSubjects.data}
          loading={topSubjects.loading}
          error={topSubjects.error}
          onRetry={topSubjects.retry}
          onSelectSubject={(subj) => setFilters((f) => ({ ...f, subject: subj }))}
        />
      </div>

      {/* ---- top teachers ---- */}
      <div className="mt-4">
        <TopTeachersTable
          data={topTeachers.data}
          loading={topTeachers.loading}
          error={topTeachers.error}
          onRetry={topTeachers.retry}
          onViewProfile={(id) => { window.location.href = `/TeacherProfile/${id}`; }}
        />
      </div>

      {/* ---- students + meetings stat groups ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <Panel eyebrow="תלמידים" title="סטטיסטיקות תלמידים" accent="sky" loading={studentsStats.loading} error={studentsStats.error} onRetry={studentsStats.retry} empty={!studentsStats.loading && !s}>
          {s && (
            <div>
              <StatRow icon={GraduationCap} label="תלמידים פעילים" value={s.active.value} deltaPct={s.active.deltaPct} />
              <StatRow icon={UserPlus2} label="תלמידים חדשים" value={s.newInRange.value} deltaPct={s.newInRange.deltaPct} />
              <StatRow icon={ThumbsUp} label="ביצעו הזמנה ראשונה" value={s.firstBooking.value} deltaPct={s.firstBooking.deltaPct} />
              <StatRow icon={Users} label="תלמידים חוזרים" value={s.returning.value} deltaPct={s.returning.deltaPct} />
              <StatRow icon={UserX2} label="ללא הזמנה לאחר הרשמה" value={s.noBookingAfterSignup.value} deltaPct={s.noBookingAfterSignup.deltaPct} invert />
              <StatRow icon={BookOpenCheck} label="ממוצע שיעורים לתלמיד" value={s.avgLessonsPerStudent.value} deltaPct={s.avgLessonsPerStudent.deltaPct} />
              <StatRow icon={Wallet} label="ממוצע הוצאה לתלמיד" value={`₪${s.avgSpendPerStudent.value}`} deltaPct={s.avgSpendPerStudent.deltaPct} />
              <StatRow icon={Percent} label="שיעור חזרת תלמידים" value={`${s.returnRate.value}%`} deltaPct={s.returnRate.deltaPct} />
            </div>
          )}
        </Panel>

        <Panel eyebrow="פגישות" title="פגישות ושיעורים מקוונים" subtitle="Zoom · Google Meet" accent="purple" loading={meetingsStats.loading} error={meetingsStats.error} onRetry={meetingsStats.retry} empty={!meetingsStats.loading && !mt}>
          {mt && (
            <div>
              <StatRow icon={Video} label="פגישות שנוצרו" value={mt.created.value} deltaPct={mt.created.deltaPct} />
              <StatRow icon={PlayCircle} label="פגישות שהתקיימו" value={mt.held.value} deltaPct={mt.held.deltaPct} />
              <StatRow icon={CalendarClock} label="פגישות עתידיות" value={mt.upcoming.value} />
              <StatRow icon={VideoOff} label="ללא קישור תקין" value={mt.brokenLink.value} deltaPct={mt.brokenLink.deltaPct} invert />
              <StatRow icon={Timer} label="לא התחילו בזמן" value={mt.startedLate.value} deltaPct={mt.startedLate.deltaPct} invert />
              <StatRow icon={FileVideo} label="הקלטות זמינות" value={mt.recordingsAvailable.value} deltaPct={mt.recordingsAvailable.deltaPct} />
              <StatRow icon={VideoOff} label="הקלטות חסרות" value={mt.recordingsMissing.value} deltaPct={mt.recordingsMissing.deltaPct} invert />
              <StatRow icon={AlertOctagon} label="תקלה ביצירת פגישה" value={mt.creationErrors.value} deltaPct={mt.creationErrors.deltaPct} invert />
            </div>
          )}
        </Panel>
      </div>

      {/* ---- activity feed + alerts ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <RecentActivityFeed data={activities.data} loading={activities.loading} error={activities.error} onRetry={activities.retry} />
        <AlertsPanel
          data={alerts.data}
          loading={alerts.loading}
          error={alerts.error}
          onRetry={alerts.retry}
          onResolve={resolveAlert}
        />
      </div>

      {/* ---- insights + quick actions ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <AutomaticInsights data={insights.data} loading={insights.loading} error={insights.error} onRetry={insights.retry} />
      </div>
    </div>
  );
}